import { ReactElement } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { Formik } from 'formik'
import { Button } from 'components/v2/Button'
import { Column } from '@10xLabs/react-web/components/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import Heading from 'components/v2/Heading'
import Modal from 'components/v2/Modal'
import { Row } from '@10xLabs/react-web/components/Row'
import { Textarea } from 'components/v2/Textarea'
import SelectCampaigns from 'components/SelectCampaigns'
import { GTCampaignType } from 'gql/types.generated'
import { ICreateIncidenceModalForm, ICreateIncidenceModalProps } from './types'
import SelectCheckboxList from 'components/SelectCheckbox'
import { Label } from 'features/Bookings/components/Service'
import { CREATE_CAMPAIGN_SUBCAMPAIGN } from 'features/Trips/mutations-legacy'
import { useMutation } from '@apollo/client'
import { schema } from './schema'
import { Input } from 'components/Input'
import { getCampaignSubcampaignErrorMessage } from 'features/Trips/utils'

const SelectLegsLabel = styled(Label)<TSFixMe>`
  font-size: 14px;
  color: rgb(93, 92, 112);
  margin-bottom: 5px;
  font-weight: normal;
`
const messages = defineMessages({
  reasonPlaceholder: {
    id: 'trips.cancellation.reason.placeholder',
    defaultMessage: 'Reason'
  }
})

const ModalContainer = styled(Modal.Container)`
  max-width: 600px;
`

const CreateIncidenceModal = ({ closeModal, trip, onCompleted }: ICreateIncidenceModalProps): ReactElement => {
  const intl = useIntl()
  const [createSubcampaign, { loading, error }] = useMutation(CREATE_CAMPAIGN_SUBCAMPAIGN, {
    onCompleted: (res): TSFixMe => {
      closeModal()
      onCompleted({
        subcampaignId: res.createCampaignSubcampaign.id
      })
    }
  })
  const initialValues: ICreateIncidenceModalForm = {
    authorizer: '',
    campaign: {
      id: '',
      name: ''
    },
    reason: '',
    trip: {
      id: trip.id,
      legSegments: []
    }
  }
  const handleSubmit = (values: ICreateIncidenceModalForm): void => {
    createSubcampaign({
      variables: {
        input: {
          id: values.campaign.id,
          reason: values.reason,
          authorizer: values.authorizer,
          trip: {
            id: values.trip.id,
            legSegmentIDs: values.trip.legSegments.map((leg): TSFixMe => leg.id)
          }
        }
      }
    })
  }
  return (
    <ModalContainer large>
      <Modal.Header>
        <Heading.H5>
          <FormattedMessage id="trips.createIncidence.title" defaultMessage="Create incidence coupons" />
        </Heading.H5>
      </Modal.Header>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema(intl)}>
        {(props): ReactElement => {
          const { handleChange, handleBlur, touched, errors, values, setFieldValue } = props

          return (
            <form onSubmit={props.handleSubmit}>
              <Modal.Content>
                <Row>
                  <Column>
                    <Textarea
                      id="reason"
                      value={values.reason}
                      placeholder={intl.formatMessage(messages.reasonPlaceholder)}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errors={errors.reason}
                      touched={touched.reason}
                      label={<FormattedMessage id="trips.createIncidence.reason.label" defaultMessage="Reason" />}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <SelectCampaigns
                      value={values.campaign.name}
                      label={
                        <FormattedMessage id="trips.createIncidence.campaign.label" defaultMessage="Select Campaign" />
                      }
                      campaignTypes={[GTCampaignType.Incidence]}
                      onChange={(campaign): void => {
                        setFieldValue('campaign', campaign)
                      }}
                      errors={errors.campaign?.id}
                      touched={touched.campaign?.id}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <SelectLegsLabel>
                      <FormattedMessage id="trips.createIncidence.campaign.label" defaultMessage="Select Legs" />
                    </SelectLegsLabel>
                    <SelectCheckboxList
                      hasAllOption
                      values={values.trip.legSegments}
                      items={trip.legSegments}
                      campaignTypes={[GTCampaignType.Incidence]}
                      onChange={(legSegments): void => {
                        setFieldValue('trip.legSegments', legSegments)
                      }}
                      getOptionLabel={(option): string => {
                        return option.origin.name + ' - ' + option.destination.name
                      }}
                      errors={errors.trip?.legSegments as string}
                      touched={Boolean(touched.trip?.legSegments)}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <SelectLegsLabel>
                      <FormattedMessage id="trips.createIncidence.authorizer.label" defaultMessage="Authorized by" />
                    </SelectLegsLabel>
                    <Input
                      id="authorizer"
                      value={values.authorizer ?? ''}
                      placeholder="Authorized by"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                        setFieldValue('authorizer', e.target.value)
                      }}
                      onBlur={handleBlur}
                      errors={errors.authorizer}
                      touched={touched.authorizer}
                    />
                  </Column>
                </Row>
              </Modal.Content>
              <Modal.Footer>
                {error && (
                  <Modal.ErrorMessage>
                    <ErrorMessage>{intl.formatMessage(getCampaignSubcampaignErrorMessage(error))}</ErrorMessage>
                  </Modal.ErrorMessage>
                )}
                <Modal.Actions>
                  <Button kind="cancel" onClick={(): void => closeModal()}>
                    <FormattedMessage id="dictionary.close" defaultMessage="Close" />
                  </Button>
                  <Button kind="primary" loading={loading} type="submit">
                    <FormattedMessage id="trips.createIncidence.action" defaultMessage="Create" />
                  </Button>
                </Modal.Actions>
              </Modal.Footer>
            </form>
          )
        }}
      </Formik>
    </ModalContainer>
  )
}

export default CreateIncidenceModal
